import { useEffect, useMemo } from "react"
import _ from "lodash"
import {
  buildCompositeId,
  getLocationIdByFeature,
  getRelatedLocationIdsByFeature,
  getSuitablyValueBetweenBearings,
  SUFFIX_MARKER,
} from "components/IndoorMap"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import { useVenue } from "providers/venue"
import { ImdfFeatureData } from "providers/venue/types"
import { useSearchParams } from "react-router-dom"
import { NEXTOPIA_CATEGORY_TYPE } from "extensions/one-siam/constants"

type Options = {
  enabled?: boolean
  offset?: {
    bottom?: number
    left?: number
    right?: number
    top?: number
  }
}

type UsePlaceDetailMapType = (
  feature: ImdfFeatureData,
  options?: Options
) => void

export const usePlaceDetailMap: UsePlaceDetailMapType = (
  feature: ImdfFeatureData,
  options = {
    enabled: true,
    offset: { top: 0, left: 0, right: 0, bottom: 195 },
  }
) => {
  const { dataLoaded, findFeatureById } = useVenue()
  const { indoorRef, mapLoaded } = useIndoorMap()
  const { offset, enabled = true } = options
  const [searchParams] = useSearchParams()

  const { top, bottom, left, right } = offset

  const offsetOptions = useMemo(
    () => ({ top, bottom, left, right }),
    [top, bottom, left, right]
  )

  const targetId = searchParams.get("target")

  const targetFeatureFromQuery = useMemo(() => {
    if (!targetId || !findFeatureById) return null
    const foundFeature = findFeatureById(targetId)
    if (
      foundFeature &&
      (foundFeature.feature_type === "unit" ||
        foundFeature.feature_type === "kiosk")
    ) {
      return foundFeature
    }
    return null
  }, [targetId, findFeatureById])

  const targetFeature = targetFeatureFromQuery || feature

  const ready =
    indoorRef.current && mapLoaded && dataLoaded && enabled && targetFeature
  const elementIds = useMemo(() => {
    const targetId = targetFeature?.id
    const mainLocation = getLocationIdByFeature(targetFeature)
    let relatedLocationIds = []
    // TODO: Discuss expected behavior after clicking a section
    const isSection = targetFeature?.feature_type === "section"
    // Prepare related units / kiosks for highlighting in section cases
    relatedLocationIds = isSection
      ? getRelatedLocationIdsByFeature(targetFeature)
      : []

    // If `targetId` is a related location ID, use duplex target ID for highlighting; otherwise, use normal target ID
    const mainRelatedLocations = getRelatedLocationIdsByFeature(feature)
    const markerId = buildCompositeId(_.uniq([feature?.id, targetId]), {
      suffix: SUFFIX_MARKER,
    })
    const targetToHighlightingId =
      mainRelatedLocations.includes(targetId) || isSection ? markerId : targetId

    return _.compact(
      _.uniq([mainLocation, ...relatedLocationIds, targetToHighlightingId])
    )
  }, [targetFeature, feature])

  const hasOwnMarker = useMemo(
    () => ["occupant", "section"].includes(`${targetFeature?.feature_type}`),
    [targetFeature]
  )

  const ordinals = useMemo(() => {
    const correlationOrdinals = _.get(
      targetFeature,
      "properties.correlation",
      []
    )
      .map((correlation) => correlation.properties.ordinal)
      .filter(isFinite)
    return Array.isArray(correlationOrdinals) && correlationOrdinals.length > 0
      ? correlationOrdinals
      : _.get(targetFeature, "properties.ordinal")
  }, [targetFeature])

  useEffect(() => {
    if (ready) {
      indoorRef.current.changeLevelByOrdinal(ordinals)
    }
  }, [indoorRef, ready, ordinals])

  useEffect(() => {
    const indoorMap = indoorRef.current
    if (ready && indoorMap) {
      indoorMap.clearAnimations()
      indoorMap.clearNavigationGeometries()
      indoorMap.enableClick()
      indoorMap.setFeatureObject3DsOpacity(1)
      indoorMap.setSpriteMarkersOpacity(1)
      indoorMap.hideVenueObjects()
      indoorMap.disableLayersFadingOnZoom()
    }
    return () => {
      if (ready && indoorMap) {
        indoorMap.clearHighlightElements()
        indoorMap.clearHighlightObject()
        indoorMap.showVenueObjects()
        indoorMap.enableLayersFadingOnZoom()
      }
    }
  }, [indoorRef, ready])

  useEffect(() => {
    if (indoorRef.current && ready && elementIds) {
      const indoorMap = indoorRef.current

      const localCategories = _.get(feature, "properties.local_categories", [])
      const matchedLocalCategoryRef = localCategories.find(
        (category) => category?.properties?.reference === NEXTOPIA_CATEGORY_TYPE
      )?.properties?.reference
      const symbolSet = !!matchedLocalCategoryRef
        ? { symbolSet: `hilight-polygonal-${matchedLocalCategoryRef}` }
        : {}
      indoorMap.clearHighlightElements()
      indoorMap.clearHighlightObject()
      const elementIdsToHighlight = _.compact(elementIds)
      const highlightedFeature = indoorMap.setHighlightElementIds(
        elementIdsToHighlight,
        {
          highlight2DOptions: {
            defaultMarker: !hasOwnMarker,
            ...symbolSet,
          },
          highlight3DOptions: {
            ...symbolSet,
          },
        }
      )
      try {
        const extent = indoorMap.getFeatureExtent(highlightedFeature)
        const center = indoorMap.getExtentCenter(extent)
        const zoom = indoorMap.getExtentZoom(extent)
        const pitch = 40
        const newCenter = indoorMap.getTargetViewCenter(
          {
            center,
            zoom,
            pitch,
          },
          { offset: offsetOptions }
        )
        indoorMap.flyTo(newCenter, {
          zoom,
          pitch,
        })
      } catch (error) {
        const { defaultCenter, defaultZoom, defaultBearing, defaultPitch } =
          indoorRef.current
        const currentBearing = indoorRef.current.getBearing()
        const bearing = getSuitablyValueBetweenBearings(
          defaultBearing,
          currentBearing
        )
        indoorRef.current.flyTo(defaultCenter, {
          zoom: defaultZoom + 1,
          bearing,
          pitch: defaultPitch,
        })
        console.warn("Can not highligh the given elements")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementIds, hasOwnMarker, indoorRef, ready, offsetOptions])
}
